/**
 * BAQsTab — Knowledge Base "BAQs" surface (PR7).
 *
 * Lets users browse the registered Epicor BAQ roster without going
 * through chat. Calls list_baqs to enumerate; click a row to expand and
 * call describe_baq for columns + sample + cached description.
 *
 * The "Try in chat" button hands a draft prompt to the parent so users
 * can run the BAQ in-context with the agent.
 */

import { useEffect, useMemo, useState, useCallback } from 'react'
import {
  Loader2, RefreshCw, Search, AlertCircle, ChevronRight, ChevronDown,
  PlayCircle, Sparkles,
} from 'lucide-react'
import { executeTool } from '../../lib/api'

function fmtDate(s) {
  if (!s) return ''
  const d = new Date(s)
  return Number.isNaN(d.getTime()) ? s : d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })
}

export default function BAQsTab({ onTryInChat }) {
  const [baqs, setBaqs] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [query, setQuery] = useState('')
  const [expanded, setExpanded] = useState(() => new Set())
  const [detailById, setDetailById] = useState({}) // id → {loading, data, error}

  const load = useCallback(async (filter = '') => {
    setLoading(true)
    setError(null)
    try {
      const resp = await executeTool('list_baqs', { query: filter || undefined, limit: 200 })
      const inner = resp?.result ?? resp ?? {}
      if (inner.error) {
        // Surface the full diagnostic (top-level error + per-strategy errors
        // when both fallbacks failed). Without this, the user just sees
        // "Failed to list BAQs" with no clue what to do next.
        setError({
          summary: inner.error,
          message: inner.message,
          getlist_error: inner.getlist_error,
          odata_error: inner.odata_error,
        })
        setBaqs([])
      } else {
        setBaqs(inner.baqs || [])
      }
    } catch (e) {
      setError({ summary: String(e?.message || e) })
      setBaqs([])
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load('') }, [load])

  // Local client-side narrowing on top of whatever the server returned.
  // Faster than re-firing list_baqs for every keystroke.
  const filtered = useMemo(() => {
    if (!baqs) return null
    if (!query.trim()) return baqs
    const q = query.toLowerCase()
    return baqs.filter(b =>
      (b.id || '').toLowerCase().includes(q) ||
      (b.description || '').toLowerCase().includes(q),
    )
  }, [baqs, query])

  async function toggleExpand(baqId) {
    setExpanded(prev => {
      const next = new Set(prev)
      if (next.has(baqId)) next.delete(baqId)
      else next.add(baqId)
      return next
    })
    // Fetch detail on first expand
    if (!detailById[baqId]) {
      setDetailById(prev => ({ ...prev, [baqId]: { loading: true } }))
      try {
        const resp = await executeTool('describe_baq', { baq_id: baqId, include_sample: true })
        const inner = resp?.result ?? resp ?? {}
        setDetailById(prev => ({ ...prev, [baqId]: { loading: false, data: inner } }))
      } catch (e) {
        setDetailById(prev => ({ ...prev, [baqId]: { loading: false, error: String(e?.message || e) } }))
      }
    }
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Business Activity Queries
          </h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-xl">
            Every BAQ registered in Epicor that Trendy can run. Click a row to see
            columns, sample data, and a description. Use the chat to run any of them —
            Trendy will auto-pick the right one when you describe what you want.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load(query)}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay disabled:opacity-50 flex-shrink-0"
        >
          {loading ? <Loader2 size={12} className="animate-spin" /> : <RefreshCw size={12} />}
          Refresh
        </button>
      </div>

      <div className="relative mb-4">
        <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="search"
          value={query}
          onChange={e => setQuery(e.target.value)}
          placeholder="Filter by BAQ ID or description (e.g., COM, aging, dashboard)…"
          className="w-full text-xs pl-7 pr-2 py-2 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-surface-dark-raised focus:outline-none focus:ring-1 focus:ring-amtrend-blue"
        />
      </div>

      {error && (
        <div className="mb-3 p-3 rounded bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-900 text-sm text-red-700 dark:text-red-300">
          <div className="flex items-start gap-2">
            <AlertCircle size={14} className="mt-0.5 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{error.summary || String(error)}</div>
              {error.message && (
                <div className="mt-1 text-xs opacity-90">{error.message}</div>
              )}
              {(error.getlist_error || error.odata_error) && (
                <details className="mt-2">
                  <summary className="text-[11px] uppercase tracking-wide font-semibold cursor-pointer opacity-80">Underlying errors</summary>
                  <div className="mt-1 space-y-1 text-[11px] font-mono break-all">
                    {error.getlist_error && (
                      <div><span className="opacity-60">GetList:</span> {error.getlist_error}</div>
                    )}
                    {error.odata_error && (
                      <div><span className="opacity-60">OData:</span> {error.odata_error}</div>
                    )}
                  </div>
                </details>
              )}
            </div>
          </div>
        </div>
      )}

      {loading && baqs === null ? (
        <div className="py-12 flex items-center justify-center text-gray-400">
          <Loader2 size={20} className="animate-spin mr-2" /> Loading BAQ registry…
        </div>
      ) : filtered && filtered.length === 0 ? (
        <div className="text-center py-12 text-sm text-gray-500 dark:text-gray-400">
          {query ? `No BAQs match "${query}".` : 'No BAQs found in the Epicor registry.'}
        </div>
      ) : filtered ? (
        <>
          <div className="text-[11px] text-gray-500 dark:text-gray-400 mb-2 tabular-nums">
            {filtered.length} BAQ{filtered.length === 1 ? '' : 's'}
            {query && baqs && baqs.length !== filtered.length && ` of ${baqs.length}`}
          </div>
          <ul className="space-y-1.5">
            {filtered.map(b => (
              <BAQRow
                key={b.id}
                baq={b}
                expanded={expanded.has(b.id)}
                detail={detailById[b.id]}
                onToggle={() => toggleExpand(b.id)}
                onTryInChat={onTryInChat}
              />
            ))}
          </ul>
        </>
      ) : null}
    </div>
  )
}

function BAQRow({ baq, expanded, detail, onToggle, onTryInChat }) {
  const hasDesc = !!(baq.description && baq.description.trim())
  return (
    <li className="border border-gray-200 dark:border-gray-800 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-start gap-2 hover:bg-gray-50 dark:hover:bg-surface-dark-raised text-left"
      >
        <span className="mt-0.5 text-gray-400">
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-mono font-medium text-gray-900 dark:text-gray-100 truncate">
              {baq.id}
            </span>
            {baq.system_flag && (
              <span className="px-1 rounded bg-gray-100 dark:bg-gray-800 text-[10px] uppercase font-semibold text-gray-500 dark:text-gray-400">system</span>
            )}
            {baq.shared && (
              <span className="px-1 rounded bg-blue-50 dark:bg-blue-950 text-[10px] uppercase font-semibold text-blue-700 dark:text-blue-300">shared</span>
            )}
            {baq.last_modified && (
              <span className="ml-auto text-[10px] text-gray-400 dark:text-gray-500">{fmtDate(baq.last_modified)}</span>
            )}
          </div>
          <div className={'mt-0.5 text-xs ' + (hasDesc ? 'text-gray-600 dark:text-gray-300' : 'text-gray-400 italic')}>
            {hasDesc ? baq.description : 'No description on file — expand to discover columns from a sample run.'}
          </div>
        </div>
      </button>
      {expanded && (
        <div className="px-3 py-2 border-t border-gray-100 dark:border-gray-900 bg-gray-50/50 dark:bg-surface-dark-raised/50 text-[11px]">
          <BAQDetail baq={baq} detail={detail} onTryInChat={onTryInChat} />
        </div>
      )}
    </li>
  )
}

function BAQDetail({ baq, detail, onTryInChat }) {
  if (!detail || detail.loading) {
    return (
      <div className="flex items-center gap-1.5 text-gray-400">
        <Loader2 size={11} className="animate-spin" /> Loading BAQ detail…
      </div>
    )
  }
  if (detail.error) {
    return (
      <div className="text-red-600 dark:text-red-400">
        Couldn&apos;t load detail: {detail.error}
      </div>
    )
  }
  const d = detail.data || {}
  const cols = d.columns || []
  const samples = d.sample_records || []
  return (
    <div className="space-y-2">
      {d.cached_description && (
        <div className="flex items-start gap-1.5 p-2 rounded bg-amtrend-blue/5 border border-amtrend-blue/20">
          <Sparkles size={11} className="mt-0.5 text-amtrend-blue flex-shrink-0" />
          <div>
            <div className="text-[10px] uppercase tracking-wide font-semibold text-amtrend-blue mb-0.5">AI summary</div>
            <div className="text-gray-700 dark:text-gray-200">{d.cached_description}</div>
          </div>
        </div>
      )}

      {d.registry_warning && (
        <div className="text-yellow-700 dark:text-yellow-300 italic">{d.registry_warning}</div>
      )}
      {d.sample_error && (
        <div className="text-yellow-700 dark:text-yellow-300 italic">{d.sample_error}</div>
      )}

      <div>
        <div className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 mb-1">
          Columns ({cols.length})
        </div>
        {cols.length === 0 ? (
          <div className="text-gray-400 italic">No columns derived from sample. The BAQ may require parameters.</div>
        ) : (
          <div className="flex flex-wrap gap-1">
            {cols.map(c => (
              <span
                key={c.name}
                title={c.sample_value != null ? `Sample: ${c.sample_value}` : ''}
                className={
                  'inline-flex items-center gap-1 px-1.5 py-0.5 rounded font-mono text-[10px] border ' +
                  (c.looks_numeric
                    ? 'bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-300 border-green-200 dark:border-green-900'
                    : c.looks_date
                    ? 'bg-purple-50 dark:bg-purple-950 text-purple-700 dark:text-purple-300 border-purple-200 dark:border-purple-900'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700')
                }
              >
                {c.name}
              </span>
            ))}
          </div>
        )}
      </div>

      {samples.length > 0 && (
        <details>
          <summary className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 cursor-pointer">Sample rows ({samples.length})</summary>
          <pre className="mt-1 p-2 rounded bg-white dark:bg-surface-dark border border-gray-200 dark:border-gray-800 overflow-auto max-h-48 text-[10px]">
            {JSON.stringify(samples, null, 2)}
          </pre>
        </details>
      )}

      <div className="flex items-center gap-2 pt-1">
        <button
          type="button"
          onClick={() => onTryInChat && onTryInChat(`Run BAQ ${baq.id}`)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium bg-amtrend-blue text-white hover:bg-blue-700"
        >
          <PlayCircle size={11} /> Run this BAQ
        </button>
        <button
          type="button"
          onClick={() => onTryInChat && onTryInChat(`What does BAQ ${baq.id} do?`)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-medium border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-surface-dark-overlay"
        >
          Ask Trendy
        </button>
      </div>
    </div>
  )
}
